import { forwardRef, useCallback, useImperativeHandle, useRef, type ComponentType, type Ref } from "react";
import * as RPopover from "@radix-ui/react-popover";
import { Clock } from "lucide-react";
import { cn } from "../utils";
import { PART_CLASSES } from "./dateTimePickerStyles";
import { DateTimePickerPresets } from "./parts/DateTimePickerPresets";
import { DateTimePickerTrigger } from "./parts/DateTimePickerTrigger";
import { useDateTimePicker } from "./useDateTimePicker";
import { VARIANT_REGISTRY } from "./variants";
import type { DateTimePickerPanelProps } from "./panels/panelTypes";
import type {
  DateTimePickerHandle,
  DateTimePickerProps,
  DateTimePickerVariant,
  ValueTypeName,
} from "./types";

/** One date/time field for the app: `variant` picks the panel from `VARIANT_REGISTRY`. */
function DateTimePickerImpl<V extends DateTimePickerVariant = "single", T extends ValueTypeName = "date">(
  props: DateTimePickerProps<V, T>,
  forwardedRef: Ref<DateTimePickerHandle>,
) {
  const {
    variant = "single" as V,
    valueType = "date" as T,
    value,
    defaultValue,
    onValueChange,
    codec,
    placeholder,
    displayFormat,
    disabled,
    required,
    id,
    name,
    className,
    triggerClassName,
    classNames,
    size = "md",
    state = "default",
    clearable = false,
    presets,
    locale,
    weekStartsOn = 0,
    numberOfMonths = 1,
    showOutsideDays = true,
    fixedWeeks = true,
    captionLayout = "dropdown",
    disabledDates,
    minDate,
    maxDate,
    minuteStep = 1,
    closeOnSelect,
    onOpenChange,
    slots,
    slotProps,
    renderDay,
    renderFooter,
    renderPresetLabel,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "data-testid": testId = "datetimepicker-trigger",
  } = props;

  const entry = VARIANT_REGISTRY[variant];

  const field = useDateTimePicker({
    variant,
    valueType,
    value,
    defaultValue,
    onValueChange: onValueChange as ((v: never) => void) | undefined,
    codec,
    minDate,
    maxDate,
    disabledDates,
    weekStartsOn,
    locale,
    displayFormat,
    closeOnSelect,
    numberOfMonths,
    minuteStep,
    onOpenChange,
    clearable,
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    forwardedRef,
    () => ({
      focus: () => triggerRef.current?.focus(),
      open: () => field.setOpen(true),
      close: () => field.setOpen(false),
      clear: () => field.clear(),
      getValue: () => field.selected,
    }),
    [field],
  );

  const handleClear = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      // Without this the click reaches the trigger and opens the popover.
      event.stopPropagation();
      field.clear();
    },
    [field],
  );

  // Slots share the default parts' signatures; the trigger must forward its ref.
  const Trigger = (slots?.trigger ?? DateTimePickerTrigger) as typeof DateTimePickerTrigger;
  const Panel = (slots?.panel ?? entry.Panel) as ComponentType<DateTimePickerPanelProps>;
  const Presets = (slots?.presets ?? DateTimePickerPresets) as typeof DateTimePickerPresets;
  const Footer = slots?.footer;

  const footerContent = renderFooter?.() ?? null;

  return (
    <>
      <RPopover.Root open={field.open} onOpenChange={field.setOpen}>
        <RPopover.Trigger asChild>
          <Trigger
            ref={triggerRef}
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-required={required || undefined}
            aria-invalid={state === "error" || undefined}
            data-testid={testId}
            displayValue={field.displayValue}
            placeholder={placeholder ?? entry.defaultPlaceholder}
            hasValue={field.hasValue}
            open={field.open}
            clearable={clearable}
            onClear={handleClear}
            size={size}
            state={state}
            Icon={entry.hasCalendar ? undefined : Clock}
            className={cn(classNames?.trigger, triggerClassName, className)}
            {...(slotProps?.trigger ?? {})}
          />
        </RPopover.Trigger>

        <RPopover.Portal>
          <RPopover.Content
            ref={contentRef}
            align="start"
            sideOffset={8}
            collisionPadding={8}
            data-part="popover"
            data-testid="datetimepicker-popover"
            className={cn(PART_CLASSES.popover, classNames?.popover)}
            onPointerDownOutside={(event) => {
              const target = event.target as HTMLElement | null;
              if (target?.closest?.('[data-slot="select-content"], [data-slot="select-trigger"], [data-slot="select-item"], [role="listbox"], [role="option"]')) {
                event.preventDefault();
              }
            }}
            onOpenAutoFocus={(event) => {
              // Radix would focus a nav arrow; send focus to the roving day instead.
              event.preventDefault();
              const root = contentRef.current;
              const day = root?.querySelector<HTMLElement>('[data-day][tabindex="0"]');
              const fallback = root?.querySelector<HTMLElement>("input, [role='option']");
              (day ?? fallback)?.focus();
            }}
          >
            {presets && presets.length > 0 ? (
              <Presets
                presets={presets}
                onSelect={field.applyPreset}
                renderLabel={renderPresetLabel}
                classNames={classNames}
                {...(slotProps?.presets ?? {})}
              />
            ) : null}

            <Panel
              field={field}
              locale={locale}
              weekStartsOn={weekStartsOn}
              showOutsideDays={showOutsideDays}
              fixedWeeks={fixedWeeks}
              captionLayout={captionLayout}
              minuteStep={minuteStep}
              classNames={classNames}
              renderDay={renderDay}
              onEscape={() => field.setOpen(false)}
              {...(slotProps?.panel ?? {})}
            />

            {Footer ? (
              <div
                data-part="footer"
                className={cn(PART_CLASSES.footer, classNames?.footer)}
              >
                <Footer {...(slotProps?.footer ?? {})} />
              </div>
            ) : footerContent ? (
              <div
                data-part="footer"
                className={cn(PART_CLASSES.footer, classNames?.footer)}
              >
                {footerContent}
              </div>
            ) : null}
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>

      {name ? <input type="hidden" name={name} value={field.formValue} aria-hidden /> : null}
    </>
  );
}

/** `forwardRef` erases generics; the cast restores the per-variant narrowing. */
export const DateTimePicker = forwardRef(DateTimePickerImpl) as <
  V extends DateTimePickerVariant = "single",
  T extends ValueTypeName = "date",
>(
  props: DateTimePickerProps<V, T> & { ref?: Ref<DateTimePickerHandle> },
) => React.ReactElement;
